<template>
  <AdminLayout>
    <v-row>
      <v-col cols="12">
        <v-alert
          class="custom-alert"
          v-if="!eventCountCheck"
          type="info"
          elevation="2"
        >
          Sie haben die maximale Anzahl an öffentlichen Events erreicht.
          Erweitern Sie Ihr Kontingent, oder löschen Sie nicht mehr benötigte
          Events.
        </v-alert>
      </v-col>
    </v-row>
    <EventSearchAndFilter
      @update:search="searchQuery = $event"
      @update:filters="filters = $event"
    />
    <v-row class="align-stretch pb-15" :gutter="16">
      <v-col
        cols="12"
        sm="6"
        md="4"
        lg="3"
        v-for="event in filteredEvents.slice()"
        :key="event.id"
        class="d-flex"
      >
        <v-skeleton-loader type="card" :loading="loading">
          <event-card
            :item="event"
            :from-route="$router.currentRoute.name"
            @delete="removeBookable(event.id)"
            @duplicate="duplicateEvent(event.id)"
          ></event-card>
        </v-skeleton-loader>
      </v-col>
    </v-row>
    <v-fab-transition style="bottom: 15px; right: 20px">
      <div class="position-fixed bottom-0 right-0 mb-4 mr-5">
        <v-menu :disabled="createDisabled">
          <template v-slot:activator="{ props }">
            <div class="split-btn">
              <v-btn
                rounded
                size="large"
                color="primary"
                class="main-btn"
                :to="{ name: buttonTarget?.to }"
              >
                <v-icon>mdi-plus</v-icon>Veranstaltung Erstellen</v-btn
              >
              <v-btn
                v-bind="props"
                rounded
                size="large"
                color="primary"
                class="actions-btn"
                ><v-icon start>mdi-menu-down</v-icon></v-btn
              >
            </div>
          </template>

          <v-list>
            <v-list-item
              v-for="(item, index) in items"
              :key="index"
              :to="{ name: item.to }"
            >
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-fab-transition>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import EventCard from "@/components/EventCard";
import { mapGetters, mapActions } from "vuex";
import ApiEventService from "@/services/api/ApiEventService";
import ToastService from "@/services/ToastService";
import BookablePermissionService from "@/services/permissions/BookablePermissionService";
import EventSearchAndFilter from "@/components/Filters/EventSearchAndFilter.vue";
import { slice } from "lodash";

export default {
  components: {
    AdminLayout,
    EventCard,
    EventSearchAndFilter,
  },
  data() {
    return {
      api: {
        events: [],
        tags: [],
      },
      filters: {},
      searchQuery: "",
      hidePastEvents: true,
      eventCountCheck: true,
      buttonTarget: null,
      activeTarget: null,
      items: [
        {
          title: "Detaillierte Event-Erstellung",
          to: "event-create-information",
        },
        { title: "Einfache Event-Erstellung", to: "simple-event-creator" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenant: "tenants/currentTenant",
      tenantId: "tenants/currentTenantId",
    }),
    createDisabled() {
      return !this.BookablePermissionService.allowCreate();
    },
    BookablePermissionService() {
      return BookablePermissionService;
    },
    filteredEvents() {
      const f = this.filters || {};

      return this.api.events
        .filter((event) => {
          const info = event.information || {};
          const address = event.eventAddress || {};
          const search = this.searchQuery?.toLowerCase() || "";
          if (this.searchQuery) {
            const matchesSearch =
              (info.name && info.name.toLowerCase().includes(search)) ||
              (info.description &&
                info.description.toLowerCase().includes(search)) ||
              (address.street &&
                address.street.toLowerCase().includes(search)) ||
              (address.houseNumber &&
                address.houseNumber.toLowerCase().includes(search)) ||
              (address.additional &&
                address.additional.toLowerCase().includes(search)) ||
              (address.city && address.city.toLowerCase().includes(search)) ||
              (address.zip && address.zip.toLowerCase().includes(search));
            if (!matchesSearch) return false;
          }
          if (f.selectedTags && f.selectedTags.length > 0) {
            const hasAllTags = f.selectedTags.every((tag) =>
              info.tags?.includes(tag)
            );
            if (!hasAllTags) return false;
          }
          if (f.isPublic !== null && event.isPublic !== f.isPublic)
            return false;
          if (f.isPaid !== null && event.attendees.free === f.isPaid)
            return false;
          if (
            f.needsRegistration !== null &&
            event.attendees.needsRegistration !== f.needsRegistration
          )
            return false;
          if (f.validFrom || f.validTo) {
            const eventStart = info.startDate ? new Date(info.startDate) : null;
            const eventEnd = info.endDate ? new Date(info.endDate) : null;
            const filterStart = f.validFrom ? new Date(f.validFrom) : null;
            const filterEnd = f.validTo ? new Date(f.validTo) : null;

            if (filterStart && (!eventStart || eventStart < filterStart))
              return false;
            if (filterEnd && (!eventEnd || eventEnd > filterEnd)) return false;
          }
          if (f.hidePastEvents) {
            const now = new Date();
            const eventEnd = info.endDate ? new Date(info.endDate) : null;
            if (eventEnd && eventEnd < now) return false;
          }

          return true;
        })
        .sort((a, b) => {
          return (
            new Date(b.information.startDate) -
            new Date(a.information.startDate)
          );
        });
    },
  },
  watch: {
    tenantId() {
      this.fetchEvents();
      this.fetchFilterTags();
    },
  },
  methods: {
    slice,
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
      addToast: "toasts/add",
    }),
    setButtonTarget(target) {
      this.buttonTarget = target;
      // Update activeTarget based on the selected item
      this.activeTarget = this.items.findIndex((item) => item.to === target.to);
    },
    remove(item) {
      this.filters.splice(this.filters.indexOf(item), 1);
    },
    async removeBookable(eventId) {
      await this.startLoading("fetch-delete-event");
      ApiEventService.deleteEvent(eventId)
        .then(() => {
          this.fetchEvents();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("event.delete.success", "success")
          );
          this.stopLoading("fetch-delete-event");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast("errors.something-wrong", "error")
          );
          console.log(error);
        });
      await this.getEventCount();
    },
    async duplicateEvent(eventId) {
      await this.startLoading("fetch-duplicate-event");
      ApiEventService.duplicateEvent(eventId)
        .then(() => {
          this.fetchEvents();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("event.duplicate.success", "success")
          );
          this.stopLoading("fetch-duplicate-event");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast(
              "event.duplicate.errors.something-wrong",
              "error"
            )
          );
          console.log(error);
        });
      await this.getEventCount();
    },
    fetchFilterTags() {
      ApiEventService.getTags()
        .then((response) => {
          this.api.tags = response.data;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    fetchEvents() {
      this.startLoading("fetch-events");
      ApiEventService.getEvents()
        .then((response) => {
          this.api.events = response.data;
        })
        .finally(() => {
          this.stopLoading("fetch-events");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getEventCount() {
      this.eventCountCheck = await ApiEventService.publicEventCountCheck();
    },
    setInitialButtonTarget() {
      if (this.tenant?.defaultEventCreationMode === "simple") {
        this.buttonTarget = this.items[1];
        this.activeTarget = 1;
      } else {
        this.buttonTarget = this.items[0];
        this.activeTarget = 0;
      }
    },
  },
  async mounted() {
    await this.getEventCount();
    this.setInitialButtonTarget();
  },
  created() {
    this.fetchEvents();
    this.fetchFilterTags();
  },
};
</script>

<style scoped>
.main-btn {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-right: 2px !important;
}
.actions-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  padding: 0 !important;
  min-width: 35px !important;
  margin-left: 0.5px;
}
.split-btn {
  display: inline-block;
}

.filter-field {
  border-radius: 15px;
}

.custom-alert {
  border-radius: 15px !important;
}
</style>
